import { ColumnDef } from "@tanstack/react-table";
import classNames from "classnames";
import React, { useMemo, useState } from "react";
import { toast } from "react-toastify";

import { SettingsCard } from "../../../components/card/SettingsCard";
import { LottieLoading } from "../../../components/graphics/LottieLoading";
import { SettingsConfirmModal } from "../../../components/modal/SettingsConfirmModal";
import { TrashSvg } from "../../../components/svg/TrashSvg";
import { Table } from "../../../components/table/Table";
import { getLinkedFirm } from "../../../sessionStorage/sessionStorage";
import {
  useCancelInvitationMutation,
  useGetTeamInvitationsQuery,
  useResendInvitationMutation,
} from "../../../slices/UserSlice";
import { escapeHtml } from "../../../utils/escapeHtml";
import { InviteButton } from "./invites/InviteButton";
import {
  initialsOf,
  inviteLink,
  inviteName,
  inviteStatusPill,
  StatusPill,
  TeamInvitation,
} from "./invites/teamInvites.utils";
import { TeamLayout } from "./TeamLayout";
import { TeamTabs } from "./TeamTabs";

const PILL: Record<StatusPill["tone"], string> = {
  blue: "bg-[#EEF4FF] text-[#2F6FDB]",
  green: "bg-[#E3F6EC] text-[#1E7F4A]",
  amber: "bg-[#FFF6E5] text-[#B7791F]",
  red: "bg-[#FDECEC] text-[#C0392B]",
};

const SMALL_BUTTON =
  "h-8 rounded-lg border border-[#DCE2EA] bg-white px-3 text-[13px] font-semibold text-[#1F2433] hover:bg-[#F8F9FB] disabled:cursor-not-allowed disabled:opacity-60";

/**
 * Settings > Team > Invitations (owner 2026-10-25): who was invited, whether they opened the link,
 * Resend / Copy link / Cancel; an expired one gets "Send a new link" / Remove.
 */
export const InvitedUsers = () => {
  const orgId = getLinkedFirm()?.orgId || "";
  const { data, isLoading, isError } = useGetTeamInvitationsQuery(orgId, {
    skip: !orgId,
  });
  const [resend, { isLoading: isResending, originalArgs: resendArgs }] =
    useResendInvitationMutation();
  const [cancel, { isLoading: isCancelling }] = useCancelInvitationMutation();
  const [cancelInvite, setCancelInvite] = useState<TeamInvitation | null>(null);

  const rows = useMemo(
    () => (data || []).filter((invite) => invite.status !== "accepted"),
    [data],
  );

  const doResend = async (invite: TeamInvitation) => {
    try {
      await resend({ id: invite.id, orgId }).unwrap();
      toast(`A new invitation email is on its way to ${inviteName(invite)}.`, {
        type: "success",
      });
    } catch (error) {
      toast(
        typeof error === "string"
          ? error
          : "The invitation couldn't be sent. Please try again.",
        {
          type: "error",
        },
      );
    }
  };

  const copyLink = async (invite: TeamInvitation) => {
    const link = inviteLink(window.location.origin, invite.id);
    try {
      await navigator.clipboard.writeText(link);
      toast(
        "Link copied. Anyone with this link can join as this person, so send it only to them.",
        {
          type: "success",
        },
      );
    } catch {
      window.prompt("Copy this link:", link);
    }
  };

  const columns = useMemo<ColumnDef<TeamInvitation>[]>(
    () => [
      {
        id: "name",
        header: "Name",
        accessorFn: (row) => inviteName(row),
        sortingFn: "alphanumeric",
        cell: ({ row }) => (
          <div className={"flex max-w-[260px] items-center gap-2"}>
            <span
              className={
                "flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#9AA0AE] text-[10px] font-semibold text-white"
              }>
              {initialsOf(row.original.firstName, row.original.lastName)}
            </span>
            <span className={"truncate font-semibold text-[#1D2C3B]"}>
              {inviteName(row.original)}
            </span>
          </div>
        ),
        meta: { className: "py-2" },
      },
      {
        accessorKey: "email",
        header: "Email",
        sortingFn: "alphanumeric",
        meta: { className: "text-[#1D2C3B] font-semibold py-2" },
      },
      {
        id: "role",
        header: "Role",
        accessorFn: (row) => row.accessLabel,
        sortingFn: "alphanumeric",
        cell: ({ row }) => (
          <span
            className={
              "inline-flex items-center gap-1.5 rounded-md border border-[#E4E9F2] px-2 py-0.5 text-xs font-medium text-[#1D2C3B]"
            }>
            <span className={"h-1.5 w-1.5 rounded-full bg-[#9AA0AE]"} />
            {row.original.accessLabel}
          </span>
        ),
        meta: { className: "py-2" },
      },
      {
        id: "status",
        header: "Status",
        accessorFn: (row) => row.sentAt,
        sortingFn: "alphanumeric",
        cell: ({ row }) => {
          const pill = inviteStatusPill(row.original);
          return (
            <span
              className={classNames(
                "rounded-full px-2.5 py-1 text-xs font-bold",
                PILL[pill.tone],
              )}>
              {pill.text}
            </span>
          );
        },
        meta: { className: "py-2" },
      },
      {
        id: "actions",
        header: "",
        enableSorting: false,
        cell: ({ row }) => {
          const invite = row.original;
          const busy = isResending && resendArgs?.id === invite.id;
          return invite.status === "expired" ? (
            <div className={"flex justify-end gap-1.5"}>
              <button
                type={"button"}
                disabled={busy}
                onClick={() => void doResend(invite)}
                className={
                  "h-8 rounded-lg bg-[#6B4EFF] px-3 text-[13px] font-semibold text-white hover:bg-[#5A3EEB] disabled:opacity-60"
                }>
                {busy ? "Sending…" : "Send a new link"}
              </button>
              <button
                type={"button"}
                onClick={() => setCancelInvite(invite)}
                className={classNames(SMALL_BUTTON, "text-[#C0392B]")}>
                Remove
              </button>
            </div>
          ) : (
            <div className={"flex justify-end gap-1.5"}>
              <button
                type={"button"}
                disabled={busy}
                onClick={() => void doResend(invite)}
                className={SMALL_BUTTON}>
                {busy ? "Sending…" : "Resend"}
              </button>
              <button
                type={"button"}
                onClick={() => void copyLink(invite)}
                className={SMALL_BUTTON}>
                Copy link
              </button>
              <button
                type={"button"}
                onClick={() => setCancelInvite(invite)}
                className={classNames(SMALL_BUTTON, "text-[#C0392B]")}>
                Cancel
              </button>
            </div>
          );
        },
        meta: { className: "py-2" },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isResending, resendArgs],
  );

  return (
    <>
      <TeamLayout>
        <SettingsCard extraClasses={"p-4"}>
          <div className={"mb-4 flex flex-wrap justify-between gap-4"}>
            <TeamTabs activeTab={"invited-users"} />
            <InviteButton />
          </div>

          <div className={"min-h-[220px]"}>
            {isLoading ? (
              <LottieLoading />
            ) : isError ? (
              <div className={"py-10 text-center text-sm text-[#8A90A2]"}>
                The invitations couldn&apos;t be loaded. Please refresh the
                page.
              </div>
            ) : rows.length === 0 ? (
              <div className={"py-12 text-center"}>
                <div className={"text-[15px] font-semibold text-[#1D2C3B]"}>
                  No invitations waiting
                </div>
                <div className={"mt-1 text-sm text-[#8A90A2]"}>
                  People you invite show here until they join.
                </div>
              </div>
            ) : (
              <Table
                data={rows}
                columns={columns}
                initialSorting={[{ id: "name", desc: false }]}
              />
            )}
          </div>
        </SettingsCard>
      </TeamLayout>
      {cancelInvite && (
        <SettingsConfirmModal
          onClose={() => setCancelInvite(null)}
          onConfirm={async () => {
            try {
              await cancel({ id: cancelInvite.id }).unwrap();
              setCancelInvite(null);
            } catch {
              toast("The invitation couldn't be removed. Please try again.", {
                type: "error",
              });
            }
          }}
          isLoading={isCancelling}
          icon={<TrashSvg />}
          title={`${
            cancelInvite.status === "expired" ? "Remove" : "Cancel"
          } the invitation for ${inviteName(cancelInvite)}?`}
          description={escapeHtml(
            "Their invitation link will stop working. You can invite them again later.",
          )}
          confirmText={
            cancelInvite.status === "expired" ? "Remove" : "Cancel invitation"
          }
          variant={"danger"}
        />
      )}
    </>
  );
};
